import classnames from 'classnames'
import * as React from 'react'

import { Icon, IconNameType, isValidIconName } from './icon'
import { PropsWithAs } from './types'

export const BUTTON_DEFAULT_ELEMENT = 'button'

export const BUTTON_BASE_CLASS = 'flex items-center font-medium whitespace-nowrap focus:outline-none'
export const BUTTON_SQUARE_BASE_CLASS = 'shrink-0 justify-center'
export const BUTTON_FOCUS_STYLES = 'focus-visible:ring-2 ring-gray-200 dark:ring-gray-500'

export enum ButtonSize {
  none = '', // Do not set fixed dimensions
  small = 'text-2xs h-6 px-2', // 24px height
  regular = 'text-2xs h-7 px-2', // 28px height
  medium = 'text-sm h-8 px-3', // 32px height
  large = 'text-sm h-9 px-4', // 36px height
}

export type ButtonSizeType = keyof typeof ButtonSize

export enum ButtonSquareSize {
  none = '', // Do not set fixed dimensions
  small = 'h-6 w-6', // 24x24px
  regular = 'h-7 w-7', // 28x28px
  medium = 'h-8 w-8', // 32x32px
  large = 'h-9 w-9', // 36x36px
}

export enum ButtonRounding {
  none = '',
  small = 'rounded', // 4px
  regular = 'rounded-md', // 6px
  full = 'rounded-full', // 9999px
}

export type ButtonRoundingType = keyof typeof ButtonRounding

export enum ButtonTheme {
  transparent, // Only basic shape is rendered
  light,
  dark,
  blue,
  blueHover,
  red,
  headerItem,
  menuItem,
  menuItemDark,
  opacity,
  outline,
  chipTransparent,
  chip,
}

export type ButtonThemeType = keyof typeof ButtonTheme

/**
 * Get button theme
 */
export function getButtonTheme(theme: ButtonTheme, active = false) {
  switch (theme) {
    case ButtonTheme.light: {
      return {
        button: classnames(
          'active:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400', // Light
          'dark:active:bg-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-300', // Dark
          {
            'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600': !active,
            'bg-gray-200 dark:bg-gray-500': active,
          }
        ),
      }
    }
    case ButtonTheme.dark: {
      return {
        button: classnames('text-gray-50 active:bg-gray-500 disabled:bg-gray-700 disabled:text-gray-300', {
          'bg-gray-700 hover:bg-gray-600': !active,
          'bg-gray-500': active,
        }),
      }
    }
    case ButtonTheme.blue: {
      return {
        button: classnames(
          'text-white hover:text-white active:bg-blue-600 disabled:bg-blue-200 dark:disabled:text-gray-300',
          {
            'bg-blue-400 hover:bg-blue-500': !active,
            'bg-blue-600': active,
          }
        ),
      }
    }
    case ButtonTheme.blueHover: {
      return {
        button: classnames(
          'active:bg-blue-600 disabled:bg-blue-200 disabled:text-gray-400 dark:disabled:text-gray-300',
          {
            'text-blue-600 hover:bg-blue-400 hover:text-white dark:text-blue-500 dark:hover:text-white': !active,
            'bg-blue-400 text-white': active,
          }
        ),
      }
    }
    case ButtonTheme.red: {
      return {
        button: classnames('text-white active:bg-red-600 disabled:bg-red-200 disabled:text-gray-300', {
          'bg-red-400 hover:bg-red-500': !active,
          'bg-red-600': active,
        }),
      }
    }
    case ButtonTheme.headerItem: {
      return {
        button: classnames(
          'bg-gray-800 text-gray-300 hover:bg-gray-900 hover:text-white disabled:bg-gray-700' // Base
        ),
        icon: 'text-white',
      }
    }
    case ButtonTheme.menuItem: {
      return {
        button: classnames({
          'hover:bg-gray-50 disabled:text-gray-400 dark:hover:bg-gray-700': !active,
          'bg-gray-50 dark:bg-gray-700': active,
        }),
      }
    }
    case ButtonTheme.menuItemDark: {
      return {
        button: classnames('text-white', {
          'hover:bg-gray-700': !active,
          'bg-gray-700': active,
        }),
      }
    }
    case ButtonTheme.opacity:
      return {
        button: classnames('disabled:opacity-50 dark:text-gray-400', {
          'opacity-70 hover:opacity-100': !active,
        }),
      }
    case ButtonTheme.outline:
      return {
        button: classnames(
          // Base styles
          'border border-gray-100 active:border-blue-400 disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-500',
          // Dark styles
          'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-50 dark:active:border-blue-400 dark:disabled:border-transparent dark:disabled:bg-gray-700 dark:disabled:text-gray-400',
          {
            'border-blue-400 dark:border-blue-400': active,
            'hover:border-gray-300 dark:hover:border-gray-400': !active,
          }
        ),
      }
    case ButtonTheme.chip:
      return {
        button: classnames('border', {
          'border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600': !active,
          'border-blue-500 bg-blue-50 text-gray-900 dark:bg-gray-700 dark:text-white': active,
        }),
      }
    case ButtonTheme.chipTransparent:
      return {
        button: classnames(
          'border', // Base styles
          {
            'border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200':
              !active,
            'border-blue-400 bg-blue-50 text-gray-900 dark:bg-gray-700 dark:text-white': active,
          }
        ),
      }
    default:
      return {}
  }
}

/**
 * Export Button html props type so we don’t always have to omit
 */
export type ButtonHTMLProps = Omit<React.HTMLProps<HTMLButtonElement>, 'ref' | 'type' | 'size' | 'content'>

export interface ButtonProps {
  /**
   * Visual appearance of the button
   */
  theme?: ButtonThemeType

  /**
   * Sets if button is square or normal.
   *
   * @default 'regular'
   */
  appearance?: 'regular' | 'square'

  /**
   * Size of the button
   *
   * @default "regular"
   */
  size?: ButtonSizeType

  /**
   * Render button in an active state (darker background, etc.)
   */
  active?: boolean

  /**
   * Sets if the button has focus ring
   *
   * @default true
   */
  focusRing?: boolean

  /**
   * Sets rounded corners for the button
   *
   * @default regular
   */
  rounding?: ButtonRoundingType

  /**
   * Class for the content wrapper
   */
  contentClass?: string

  /**
   * Name of left icon
   */
  leftContent?: JSX.Element | IconNameType

  /**
   * Name of right icon
   */
  rightContent?: JSX.Element | IconNameType
}

export type ButtonComponentProps<TagType extends React.ElementType = typeof BUTTON_DEFAULT_ELEMENT> =
  PropsWithAs<TagType> & ButtonProps

function ButtonComponent<TagType extends React.ElementType = typeof BUTTON_DEFAULT_ELEMENT>(
  {
    as: asComponent,
    theme = 'light',
    size = 'regular',
    rounding = 'regular',
    focusRing = true,
    appearance = 'regular',
    active,
    contentClass,
    leftContent,
    rightContent,
    className,
    children,
    ...props
  }: ButtonComponentProps<TagType>,
  ref: React.ForwardedRef<HTMLElement>
) {
  const isSquare = appearance === 'square'
  const buttonTheme = React.useMemo(() => getButtonTheme(ButtonTheme[theme], active), [theme, active])
  const buttonSize = isSquare ? ButtonSquareSize[size] : ButtonSize[size]
  const buttonRounding = ButtonRounding[rounding]

  return React.createElement(
    asComponent ?? BUTTON_DEFAULT_ELEMENT,
    {
      ...props,
      ref,
      className: classnames(
        BUTTON_BASE_CLASS,
        buttonTheme.button,
        buttonSize,
        buttonRounding,
        {
          [BUTTON_SQUARE_BASE_CLASS]: isSquare,
          [BUTTON_FOCUS_STYLES]: focusRing,
        },
        className
      ),
    },
    <>
      {isValidIconName(leftContent) ? (
        <Icon name={leftContent} className={classnames(buttonTheme.icon, 'shrink-0')} />
      ) : (
        leftContent
      )}
      {children && !isSquare ? (
        <div
          className={classnames(contentClass, 'grow text-left', {
            'ml-1': leftContent,
            'mr-1': rightContent,
          })}
        >
          {children}
        </div>
      ) : (
        children
      )}
      {isValidIconName(rightContent) ? (
        <Icon name={rightContent} className={classnames(buttonTheme.icon, 'shrink-0')} />
      ) : (
        rightContent
      )}
    </>
  )
}

/**
 * We need to forward ref to the function, but typescript looses the type of the
 * props param so we need to typecast to get suggestions to work
 */
export const Button = React.forwardRef(ButtonComponent) as <
  TagType extends React.ElementType = typeof BUTTON_DEFAULT_ELEMENT
>(
  props: PropsWithAs<TagType> & ButtonProps & { ref?: React.ForwardedRef<HTMLElement> }
) => ReturnType<typeof ButtonComponent>
